import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, ValidationErrors } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import FormScheduleComponent from 'webapp/components/form/schedule';
import FormSelectBoardComponent from 'webapp/components/form/select-board';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import FormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';
import TimezoneDescriptionComponent from 'webapp/components/timezone-description';

import type { Reminder } from 'webapp/models-custom';

type ValidationKeys = 'target';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: Reminder;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormReminderMainComponent extends Component<MainComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);

		if (!this.args.model.objectId) {
			const firstReportId = this.itemsList[0]?.value;
			if (firstReportId) {
				this.args.model.objectId = firstReportId;
			}
		}
	}

	get itemsList() {
		return this.mmReportsService.normalReportModels.map((report) => {
			return {
				value: report.numericId!,
				label: report.title,
				label2: report.createdText,
				avatar: report.image || report.fallbackImage,
				bgTheme: report.bgTheme,
				color: report.color,
				count: report.slideCount,
			};
		});
	}

	@action
	changeReportId(reportId: number) {
		this.args.model.objectId = reportId;
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.args.model;

		model.validate(); // to cleanup general errors

		if (!model.objectId) {
			errors.target = [{ message: 'Must choose a report!' }];
		}

		return errors;
	}

	@action
	save() {
		const model = this.args.model;

		model.cleanupUnusedScheduleValues();

		return model.save().then(() => {
			this.analyticsService.event(TrackingEvent.REPORT_REMINDER, {
				origin: this.args.origin,
				frequency: model.schedule.typeText.toLowerCase(),
			});
		});
	}

	<template>
		<div class="row row--board-selector">
			<FormSelectBoardComponent
				name="report"
				@selected={{@model.objectId}}
				@options={{this.itemsList}}
				@onChange={{this.changeReportId}}
				@placeholder="Select report"
				@isNumeric={{true}}
				@isRequired={{true}}
				@extraClass="cs-image"
				@dropdownSearchPlaceholder="Search"
				@showFloatlabel={{false}}
				@tooltipOnSelectionFromProperty=""
				@isDisabled={{@submitInProgress}}
			/>
			<RowErrorComponent @items={{@errors.target}} />
		</div>

		<FormRowComponent @label="Recipient" @className="row--text">
			A reminder email will be sent to
			<strong>{{this.currentUserService.model.email}}</strong>.
		</FormRowComponent>

		<FormScheduleComponent
			@model={{@model}}
			@showOneOff={{false}}
			@submitInProgress={{@submitInProgress}}
			@labelText="Frequency"
		/>

		<TimezoneDescriptionComponent />
	</template>
}
